import React from 'react';

interface PassengerManifestEntry {
  name: string;
  nationality: string;
  passportNumber: string;
  status: string;
  baggageWeight: string;
  comment: string;
  phone: string;
}

interface PassengerManifestModalProps {
  open: boolean;
  title: string;
  issuedAt: string | null;
  issuedBy: string | null;
  passengers: PassengerManifestEntry[];
  loading: boolean;
  error: string | null;
  issueLoading: boolean;
  canIssue: boolean;
  canDownloadHtml: boolean;
  canPrint: boolean;
  canEmail: boolean;
  emailTo: string;
  emailCc: string;
  emailBcc: string;
  emailSending: boolean;
  onEmailToChange: (value: string) => void;
  onEmailCcChange: (value: string) => void;
  onEmailBccChange: (value: string) => void;
  onClose: () => void;
  onIssue: () => void;
  onDownloadCsv: () => void;
  onDownloadHtml: () => void;
  onPrint: () => void;
  onEmail: () => void;
}

const PassengerManifestModal: React.FC<PassengerManifestModalProps> = ({
  open,
  title,
  issuedAt,
  issuedBy,
  passengers,
  loading,
  error,
  issueLoading,
  canIssue,
  canDownloadHtml,
  canPrint,
  canEmail,
  emailTo,
  emailCc,
  emailBcc,
  emailSending,
  onEmailToChange,
  onEmailCcChange,
  onEmailBccChange,
  onClose,
  onIssue,
  onDownloadCsv,
  onDownloadHtml,
  onPrint,
  onEmail,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-slate-900/70 backdrop-blur-xl p-6">
      <div className="bg-white rounded-[2.5rem] w-full max-w-xl overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Passenger List</p>
            <h3 className="text-lg font-black text-slate-900">{title || 'Passenger List'}</h3>
            {issuedAt && (
              <p className="mt-1 text-[11px] text-emerald-700 font-semibold">
                Manifest issued: {new Date(issuedAt).toLocaleString()}
                {issuedBy ? ` · ${issuedBy}` : ''}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div className="p-6">
          {!loading && !error && (
            <>
            <div className="mb-4 grid gap-2 md:grid-cols-3">
              <input
                type="email"
                value={emailTo}
                onChange={(e) => onEmailToChange(e.target.value)}
                placeholder="To (optional)"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-indigo-300"
              />
              <input
                type="text"
                value={emailCc}
                onChange={(e) => onEmailCcChange(e.target.value)}
                placeholder="CC (comma separated)"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-indigo-300"
              />
              <input
                type="text"
                value={emailBcc}
                onChange={(e) => onEmailBccChange(e.target.value)}
                placeholder="BCC (comma separated)"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-indigo-300"
              />
            </div>
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onIssue}
                disabled={!canIssue || issueLoading}
                className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${
                  issuedAt
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {issuedAt ? 'Issued' : issueLoading ? 'Issuing…' : 'Issue Manifest'}
              </button>
              <button
                type="button"
                onClick={onDownloadCsv}
                disabled={passengers.length === 0}
                className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:text-slate-300 disabled:border-slate-100"
              >
                Download CSV
              </button>
              <button
                type="button"
                onClick={onDownloadHtml}
                disabled={!canDownloadHtml}
                className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:text-slate-300 disabled:border-slate-100"
                title="Uses Outbound Hub HTML template routing"
              >
                Download Template HTML
              </button>
              <button
                type="button"
                onClick={onPrint}
                disabled={!canPrint}
                className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:text-slate-300 disabled:border-slate-100"
              >
                Print
              </button>
              <button
                type="button"
                onClick={onEmail}
                disabled={!canEmail || emailSending}
                className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:text-slate-300 disabled:border-slate-100"
              >
                {emailSending ? 'Sending…' : 'Email'}
              </button>
            </div>
            </>
          )}
          {loading && <div className="text-sm text-slate-400">Loading passenger list...</div>}
          {!loading && error && <div className="text-sm text-slate-500">{error}</div>}
          {!loading && !error && passengers.length === 0 && (
            <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
              No passengers listed yet.
            </div>
          )}
          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100">
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Nationality</th>
                    <th className="py-2 pr-3">Passport</th>
                    <th className="py-2">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {passengers.map((p, idx) => (
                    <tr key={`${p.name}-${idx}`} className="border-b border-slate-50">
                      <td className="py-2 pr-3 font-semibold text-slate-800">{p.name}</td>
                      <td className="py-2 pr-3 text-slate-600">{p.nationality || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600 font-mono text-xs">{p.passportNumber || '—'}</td>
                      <td className="py-2 text-slate-600">{p.phone || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PassengerManifestModal;
